/**
 * Game settings panel for waiting room (host can edit, others view only)
 */

interface GameSettingsPanelProps {
  turnTimerMs: number | null | undefined;
  endTimerMs: number;
  isHost: boolean;
  onUpdateSettings: (settings: { turnTimerMs?: number | null; endTimerMs?: number }) => void;
}

const TURN_TIMER_OPTIONS = [
  { value: null, label: 'Off' },
  { value: 3000, label: '3s' },
  { value: 5000, label: '5s' },
  { value: 10000, label: '10s' },
  { value: 15000, label: '15s' },
  { value: 30000, label: '30s' },
];

const END_TIMER_OPTIONS = [
  { value: 60000, label: '1 min' },
  { value: 120000, label: '2 min' },
  { value: 180000, label: '3 min' },
  { value: 300000, label: '5 min' },
];

export function GameSettingsPanel({
  turnTimerMs,
  endTimerMs,
  isHost,
  onUpdateSettings,
}: GameSettingsPanelProps) {
  const formatTurnTimer = (ms: number | null | undefined) => {
    if (ms === null || ms === undefined) return 'Off';
    return `${ms / 1000}s`;
  };

  const formatEndTimer = (ms: number) => {
    const minutes = ms / 60000;
    return `${minutes} min`;
  };

  return (
    <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-3">
      <h3 className="font-semibold text-sm text-slate-700 dark:text-slate-200">Game Settings</h3>

      {/* Turn Timer */}
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm text-slate-600 dark:text-slate-400" htmlFor="turn-timer">
          Turn Timer:
        </label>
        {isHost ? (
          <select
            id="turn-timer"
            value={turnTimerMs === null || turnTimerMs === undefined ? 'null' : turnTimerMs}
            onChange={(e) => {
              const val = e.target.value === 'null' ? null : parseInt(e.target.value, 10);
              onUpdateSettings({ turnTimerMs: val });
            }}
            className="text-sm px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {TURN_TIMER_OPTIONS.map((opt) => (
              <option key={opt.value ?? 'null'} value={opt.value ?? 'null'}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm text-slate-800 dark:text-slate-100 font-medium">
            {formatTurnTimer(turnTimerMs)}
          </span>
        )}
      </div>

      {/* End Game Timer */}
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm text-slate-600 dark:text-slate-400" htmlFor="end-timer">
          End Timer:
        </label>
        {isHost ? (
          <select
            id="end-timer"
            value={endTimerMs}
            onChange={(e) => {
              onUpdateSettings({ endTimerMs: parseInt(e.target.value, 10) });
            }}
            className="text-sm px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {END_TIMER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm text-slate-800 dark:text-slate-100 font-medium">
            {formatEndTimer(endTimerMs)}
          </span>
        )}
      </div>

      {/* Help text */}
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {turnTimerMs
          ? `Players have ${turnTimerMs / 1000}s to flip a tile on their turn.`
          : 'No time limit per turn.'}
        {' '}
        Game ends {endTimerMs / 60000} min after last tile flip.
      </p>
    </div>
  );
}
